/**
 * Plan 47 — ParentOrderingDisabledBanner.
 *
 * Renders at the top of /orders/family when a campus takes no parent
 * orders: its `orderMethods` omit PARENT (school ordering only, PROD-1554)
 * or its org's `orderingChannels` does NOT include `PARENT_ORDER`. The
 * banner tells the parent that the school manages ordering and they don't
 * need to place a cart themselves. On a school-ordering-only campus the week
 * and day views also go view-only (no deadline pills, cart or steppers), keyed
 * off `acceptsParentOrders` (`components/orders/family/week/columns.ts`).
 *
 * Copy mirrors the canonical plan's banner spec ("Your school manages
 * meal ordering for you..."). Uses the `@ordo/ui/alert` primitive in
 * `info` tone (informational, not destructive — the parent isn't
 * doing anything wrong).
 */

import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { Info } from 'lucide-react'

export interface ParentOrderingDisabledBannerProps {
  /** School name shown inline in the message body. */
  schoolName?: string | null
}

export function ParentOrderingDisabledBanner({ schoolName }: ParentOrderingDisabledBannerProps) {
  const subject = schoolName ?? 'Your school'
  return (
    <Alert variant="info" className="mb-4" data-testid="parent-ordering-disabled-banner">
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
        <div className="flex-1">
          <AlertTitle>Your school manages meal ordering for you</AlertTitle>
          <AlertDescription>
            <strong>{subject}</strong> places meal orders for all students through their cafeteria team. This menu shows
            what&apos;s being served. You don&apos;t need to place an order. If you have questions about meal
            selections, contact your school&apos;s cafeteria directly.
          </AlertDescription>
        </div>
      </div>
    </Alert>
  )
}
